var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AvatarGroup } from '@web/components/ui/AvatarGroup';
import { Tooltip } from '@web/components/ui/Tooltip';
import { ACTIVITY_COLORS } from '@web/config';
import { useUserContext } from '@web/contexts/User';
import { getUrl } from '@web/router/getUrl';
import { appsUrl } from '@web/router/urls';
import { classNames } from '@web/utility';
import { createGroupTooltip, getAvatarDisplayOptions, getFullDayTimelineRange, getStartEndWorkingHours, getUniqueUsersFromGroup, groupTimelineItems } from '@web/utility/dailyTimelineUtils';
import { useNavigateTo } from '@web/utility/hooks/useNavigateTo';
import { appsActiveTabs } from '@web/utility/tabs';
import { differenceInMinutes, format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
var PERCENT = 100;
export var TimelineSingleDay = function (_a) {
    var data = _a.data, _b = _a.fullDay, fullDay = _b === void 0 ? false : _b, _c = _a.showAvatars, showAvatars = _c === void 0 ? false : _c, _d = _a.eventsLogContext, eventsLogContext = _d === void 0 ? null : _d, getSegmentColors = _a.getSegmentColors;
    var t = useTranslation().t;
    var navigateTo = useNavigateTo();
    var currentUser = useUserContext().currentUser;
    var avatarOptions = getAvatarDisplayOptions(showAvatars);
    var getGroupKey = useMemo(function () {
        if (!getSegmentColors) {
            return undefined;
        }
        return function (item) { var _a, _b; return (_b = (_a = getSegmentColors(item)) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : item.type; };
    }, [getSegmentColors]);
    var groupedItems = useMemo(function () {
        return groupTimelineItems(data.items, avatarOptions.groupThreshold, false, getGroupKey);
    }, [data.items, avatarOptions.groupThreshold, getGroupKey]);
    if (!(currentUser === null || currentUser === void 0 ? void 0 : currentUser.workspace)) {
        return null;
    }
    var _e = currentUser.workspace, startWorkingHour = _e.startWorkingHour, endWorkingHour = _e.endWorkingHour;
    var workingHours = {
        startWorkingHour: startWorkingHour,
        endWorkingHour: endWorkingHour
    };
    var _f = fullDay
        ? getFullDayTimelineRange(data.items, workingHours)
        : getStartEndWorkingHours(data.items, workingHours), startDate = _f.startDate, endDate = _f.endDate;
    var diff = differenceInMinutes(endDate, startDate);
    return (_jsxs("div", __assign({ className: classNames('py-2 overflow-visible', showAvatars && 'pt-10') }, { children: [_jsx("div", __assign({ className: "relative h-6 overflow-visible" }, { children: _jsx("div", __assign({ className: "absolute top-0 left-0 w-full h-8 bg-gray-200 rounded-md", style: {
                        top: '50%',
                        transform: 'translateY(-50%)'
                    } }, { children: groupedItems.map(function (group, groupIndex) {
                        var _a;
                        var firstItem = group[0];
                        var segmentColors = (_a = getSegmentColors === null || getSegmentColors === void 0 ? void 0 : getSegmentColors(firstItem)) !== null && _a !== void 0 ? _a : ACTIVITY_COLORS[firstItem.type];
                        var color = segmentColors.color, hoverColor = segmentColors.hoverColor;
                        var startInMinutes = differenceInMinutes(firstItem.startDate, startDate);
                        var endInMinutes = group.reduce(function (maxEnd, item) {
                            var itemEnd = item.endDate
                                ? differenceInMinutes(item.endDate, startDate)
                                : differenceInMinutes(item.startDate, startDate) + 5;
                            return Math.max(maxEnd, itemEnd);
                        }, 0);
                        var width = ((endInMinutes - startInMinutes) * PERCENT) / diff;
                        var groupTooltip = createGroupTooltip(group, t);
                        var groupUsers = getUniqueUsersFromGroup(group);
                        var canOpenEventsLog = eventsLogContext != null;
                        var handleSegmentClick = function () {
                            if (!eventsLogContext)
                                return;
                            navigateTo({
                                pathname: getUrl(appsUrl, appsActiveTabs.eventsLog),
                                newFilters: {
                                    period: 'custom',
                                    dateFrom: eventsLogContext.dayDate,
                                    dateTo: eventsLogContext.dayDate,
                                    user: eventsLogContext.userId,
                                    app: firstItem.type,
                                    page: 1,
                                    activityLabel: null,
                                    pageSection: null,
                                    scrollY: null
                                }
                            });
                        };
                        return (_jsx("div", __assign({ className: "absolute", style: {
                                left: "".concat((startInMinutes * PERCENT) / diff, "%"),
                                width: "".concat(width, "%")
                            } }, { children: _jsxs("div", __assign({ className: "relative" }, { children: [showAvatars && groupUsers.length > 0 && (_jsx("div", __assign({ className: "absolute top-[-30px] left-1/2 -translate-x-1/2 z-10 flex items-center justify-center gap-0.5" }, { children: _jsx(AvatarGroup, { users: groupUsers.slice(0, avatarOptions.maxAvatarsPerSegment) }) }))), _jsx(Tooltip, __assign({ content: groupTooltip, place: group.length > 5 ? 'left' : 'top', className: "!w-max", style: { maxWidth: '52rem' } }, { children: _jsx("div", { className: classNames('h-8', "bg-".concat(color), hoverColor, 'rounded-md', canOpenEventsLog && 'cursor-pointer'), role: canOpenEventsLog ? 'button' : undefined, tabIndex: canOpenEventsLog ? 0 : undefined, onClick: function (e) {
                                                if (!canOpenEventsLog)
                                                    return;
                                                e.stopPropagation();
                                                handleSegmentClick();
                                            }, onKeyDown: function (e) {
                                                if (!canOpenEventsLog)
                                                    return;
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSegmentClick();
                                                }
                                            } }) }))] })) }), "group-".concat(groupIndex)));
                    }) })) })), _jsxs("div", __assign({ className: "flex justify-between items-center p-2 text-gray-600 text-sm" }, { children: [_jsx("span", { children: format(startDate, 'hh:mm a') }), _jsx("span", { children: format(endDate, 'hh:mm a') })] }))] })));
};
export default TimelineSingleDay;
